"use client";

import { useState, Suspense, useEffect, useCallback, useMemo } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import BrandsHeroSection from "./BrandsHeroSection";
import ShopHeader from "./ShopHeader";
import BrandServicesSection from "./BrandServicesSection";
import BrandRoomsSection from "./BrandRoomsSection";
import { ShopClient } from "../ShopClient";
import { CollectionGrid, CollectionStrip, type ShopCollection } from "@/components/shop/CollectionTiles";
import { inSeason } from "@/lib/config/collections";
import { StoreSelectionOverlay } from "./StoreSelectionOverlay";
import { ShopProduct, ProductPagination, ProductFilters } from "@/lib/types/shop";

/** The product grid's own filters in the address (ShopClient): a link with any of them is a link to products. */
const PRODUCT_LINK_KEYS = [
  "category", "subcategory", "productType", "search", "minPrice", "maxPrice", "sortBy",
  "sizes", "style", "color", "occasion", "material", "fitting", "onSale", "page",
];

interface BrandShopWrapperProps {
  brandData: any;
  productCount: number;
  initialProducts: ShopProduct[];
  pagination: ProductPagination;
  categories: string[];
  filterOptions: any;
  initialFilters: ProductFilters;
  brandSlug: string;
  brandName: string;
  brandId?: string;
  industry: "fashion" | "electronics";
  stores?: any[];
  showStorePickerOnLanding?: boolean;
  /** The owner's "Show services before products on my website" (brands.servicesFirstOnWebsite). */
  servicesFirst?: boolean;
}

export function BrandShopWrapper({
  brandData,
  productCount,
  initialProducts,
  pagination,
  categories,
  filterOptions,
  initialFilters,
  brandSlug,
  brandName,
  brandId,
  industry,
  stores = [],
  showStorePickerOnLanding = true,
  servicesFirst = false,
}: BrandShopWrapperProps) {
  const [activeTab, setActiveTab] = useState<string>("Products");
  const [showOverlay, setShowOverlay] = useState(false);

  // Services-as-products: bookable services power a SERVICES tab that only
  // appears when the brand actually has some (tiny public query, the same
  // one the tab's cards read).
  const brandServices = useQuery(
    api.products.websiteServices.listWebsiteServices,
    brandId ? { brandId: brandId as Id<"brands"> } : "skip",
  ) as any[] | undefined;
  const hasServices = (brandServices?.length ?? 0) > 0;
  // Accommodation: room types on sale power a ROOMS tab, first in the row.
  const publicRooms = useQuery(
    api.accommodation.rooms.listPublicRoomTypes,
    brandId ? { brandId: brandId as Id<"brands"> } : "skip",
  ) as any[] | undefined;
  const hasRooms = (publicRooms?.length ?? 0) > 0;
  // A hotel's website opens on its rooms, unless the visitor already chose a tab.
  const [tabTouched, setTabTouched] = useState(false);
  useEffect(() => {
    if (hasRooms && !tabTouched && activeTab === "Products") setActiveTab("Rooms");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRooms]);
  // A shop whose owner shows services first (Brand Settings → Storefront)
  // opens on them the same way. Once the rooms are known: a hotel still
  // opens on its rooms. A link that asks for products (a category, a
  // search, a page of them) still opens on the products.
  const roomsKnown = publicRooms !== undefined;
  useEffect(() => {
    if (!servicesFirst || !hasServices || !roomsKnown || hasRooms || tabTouched || activeTab !== "Products") return;
    const asked = new URLSearchParams(window.location.search);
    if (PRODUCT_LINK_KEYS.some((key) => asked.has(key))) return;
    setActiveTab("Services");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesFirst, hasServices, roomsKnown, hasRooms]);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [selectedStoreId, setSelectedStoreId] = useState<string | undefined>(
    initialFilters.store || searchParams?.get("store") || undefined
  );
  // The shop's own collections (Sep 2026) — the ones in season on the
  // visitor's own day: a strip above the products and a Collections tab.
  // The picked branch's and the whole business's (Zeying, 20 Oct 2026).
  const shopCollections = useQuery(
    api.collections.shop.listShopCollections,
    brandId ? { brandId: brandId as Id<"brands">, ...(selectedStoreId ? { storeId: selectedStoreId } : {}) } : "skip",
  ) as (ShopCollection & { seasonFrom: number | null; seasonTo: number | null })[] | undefined;
  const collections = useMemo(
    () => (shopCollections ?? []).filter((c) => inSeason(c.seasonFrom, c.seasonTo)),
    [shopCollections],
  );
  // A branch with no collections has no Collections tab to stay on.
  useEffect(() => {
    if (activeTab === "Collections" && shopCollections !== undefined && collections.length === 0) setActiveTab("Products");
  }, [activeTab, shopCollections, collections.length]);

  useEffect(() => {
    if (typeof window !== "undefined" && stores.length > 1) {
      const urlStore = searchParams?.get("store");
      const localStore = localStorage.getItem(`shop_store_${brandId}`);

      if (!urlStore) {
         if (localStore) {
           setSelectedStoreId(localStore);
           const newParams = new URLSearchParams(searchParams?.toString() || "");
           newParams.set("store", localStore);
           router.replace(`${pathname}?${newParams.toString()}`);
         } else if (showStorePickerOnLanding === false) {
           // Picker disabled: skip the overlay, default to the main store, let users switch via header
           const defaultStore = stores.find((s: any) => s.isDefault) || stores[0];
           if (defaultStore?._id) {
             setSelectedStoreId(defaultStore._id);
             localStorage.setItem(`shop_store_${brandId}`, defaultStore._id);
             const newParams = new URLSearchParams(searchParams?.toString() || "");
             newParams.set("store", defaultStore._id);
             router.replace(`${pathname}?${newParams.toString()}`);
           }
         } else {
           setShowOverlay(true);
         }
      } else {
         setSelectedStoreId(urlStore);
         if (localStore !== urlStore) {
            localStorage.setItem(`shop_store_${brandId}`, urlStore);
         }
      }
    }
  }, [stores, searchParams, brandId, router, pathname, showStorePickerOnLanding]);

  const handleSelectStore = (storeId: string) => {
    localStorage.setItem(`shop_store_${brandId}`, storeId);
    setSelectedStoreId(storeId);
    setShowOverlay(false);
    const newParams = new URLSearchParams(searchParams?.toString() || "");
    newParams.set("store", storeId);
    router.push(`${pathname}?${newParams.toString()}`);
  };

  // Called from the header store dropdown
  const handleHeaderStoreChange = useCallback((storeId: string) => {
    if (storeId) {
      localStorage.setItem(`shop_store_${brandId}`, storeId);
      setSelectedStoreId(storeId);
    } else {
      localStorage.removeItem(`shop_store_${brandId}`);
      setSelectedStoreId(undefined);
    }
    // Update the URL — ShopClient will pick up the store change via its own filter handler
    const newParams = new URLSearchParams(searchParams?.toString() || "");
    if (storeId) {
      newParams.set("store", storeId);
    } else {
      newParams.delete("store");
    }
    const newURL = newParams.toString() ? `${pathname}?${newParams.toString()}` : pathname;
    router.replace(newURL || "/");
  }, [brandId, searchParams, pathname, router]);

  return (
    <>
      {showOverlay && (
        <StoreSelectionOverlay 
          stores={stores} 
          onSelect={handleSelectStore} 
          brandName={brandName}
          brandImage={brandData?.profileImage}
        />
      )}
      
      {/* Header - Fixed at top with store selector */}
      <ShopHeader 
        brandName={brandName}
        stores={stores}
        selectedStoreId={selectedStoreId}
        onStoreChange={handleHeaderStoreChange}
      />

      {/* Hero Section - with top padding for fixed header */}
      <div className="pt-16">
        <BrandsHeroSection
          brandData={(() => {
            // Priority:
            // 1. Explicitly selected store
            // 2. Single store (if only one exists, it's effectively "selected")
            const effectiveStoreId = selectedStoreId || (stores.length === 1 ? stores[0]._id : undefined);
            
            if (effectiveStoreId && stores.length > 0) {
              const selectedStore = stores.find((s: any) => s._id === effectiveStoreId);
              if (selectedStore) {
                // Start with brand data but STRIP per-branch fields — the store
                // must earn each one (own value, or inherit when "same as main").
                const storeData: any = {
                  ...brandData,
                  profileImage: undefined,
                  coverImages: [],
                  description: undefined,
                  tagline: undefined,
                  socialLinks: undefined,
                  themeColor: undefined,
                  shopNumber: undefined,
                };

                // Override text fields
                if (selectedStore.name) storeData.name = selectedStore.name;
                if (selectedStore.address?.formattedAddress) storeData.location = selectedStore.address.formattedAddress;
                else if (selectedStore.address?.city) storeData.location = `${selectedStore.address.city}, ${selectedStore.address.country || ''}`;

                // Determine image source:
                // 1. Store has its OWN images → use them
                // 2. Store has useMainStoreBranding=true → inherit from brand
                // 3. Neither → show empty/default state (no images)
                const hasOwnProfile = !!selectedStore.profileImage;
                const hasOwnCovers = selectedStore.coverImages && selectedStore.coverImages.length > 0;
                const hasOwnDescription = !!selectedStore.description;
                // The main shop's face lives on the brand record (Store Settings
                // writes it there), so picking the main shop shows the brand's
                // logo — not an empty "Z" (Zeying, Oct 2026).
                const inheritFromMain = selectedStore.useMainStoreBranding === true || stores.length === 1 || selectedStore.isDefault === true;

                // FIX (2026-06-27): when the store MIRRORS main (single store, or
                // "same as main branch"), the BRAND's profileImage must win. A stale
                // per-store override was shadowing the logo uploaded in Store Settings
                // (which writes the brand record for the main store) — so POS showed the
                // new logo but the shop kept rendering the old store override. A branch
                // with its OWN identity (not inheriting) still uses its own image.
                if (inheritFromMain) {
                  storeData.profileImage = brandData?.profileImage;
                } else if (hasOwnProfile) {
                  storeData.profileImage = selectedStore.profileImage;
                }

                if (hasOwnCovers) {
                  storeData.coverImages = selectedStore.coverImages;
                } else if (inheritFromMain) {
                  storeData.coverImages = brandData?.coverImages || [];
                }

                if (hasOwnDescription) {
                  storeData.description = selectedStore.description;
                } else if (inheritFromMain) {
                  storeData.description = brandData?.description;
                }

                // Tagline (own → inherit brand)
                if (selectedStore.tagline) {
                  storeData.tagline = selectedStore.tagline;
                } else if (inheritFromMain) {
                  storeData.tagline = brandData?.tagline;
                }

                // Social links: store keeps its own socialMedia; map it to the
                // hero's socialLinks shape. Otherwise inherit the brand's links.
                const sm = selectedStore.socialMedia;
                const hasOwnSocial = !!sm && Object.values(sm).some((v) => !!v);
                if (hasOwnSocial) {
                  storeData.socialLinks = {
                    website: sm.website,
                    instagram: sm.instagram,
                    twitter: sm.twitter,
                    facebook: sm.facebook,
                    tiktok: sm.tiktok,
                    whatsapp: sm.whatsapp,
                    youtube: sm.youtube,
                  };
                } else if (inheritFromMain) {
                  storeData.socialLinks = brandData?.socialLinks;
                }

                // Theme colour (own → inherit brand)
                if (selectedStore.themeColor) {
                  storeData.themeColor = selectedStore.themeColor;
                } else if (inheritFromMain) {
                  storeData.themeColor = brandData?.themeColor;
                }

                // Shop number lives on the branch's address (physical to the branch);
                // inherit the brand's only when "same as main branch" is on.
                if (selectedStore.address?.shopNumber) {
                  storeData.shopNumber = selectedStore.address.shopNumber;
                } else if (inheritFromMain) {
                  storeData.shopNumber = brandData?.shopNumber;
                }

                return storeData;
              }
            }
            return brandData;
          })()}
          productCount={productCount}
          onTabChange={(tab) => { setTabTouched(true); setActiveTab(tab); }}
          activeTab={activeTab}
          domain={brandSlug}
          brandId={brandId}
          hasServices={hasServices}
          servicesFirst={servicesFirst}
          hasRooms={hasRooms}
          collectionCount={collections.length}
        />
      </div>

      {/* Products Tab */}
      {activeTab === "Products" && (
        <Suspense fallback={null}>
          <ShopClient
            initialProducts={initialProducts}
            pagination={pagination}
            categories={categories}
            filterOptions={filterOptions}
            initialFilters={initialFilters}
            brandSlug={brandSlug}
            brandName={brandName}
            brandId={brandId}
            industry={industry}
            stores={stores}
            beforeGrid={collections.length > 0 ? <CollectionStrip collections={collections} storeId={selectedStoreId} /> : null}
          />
        </Suspense>
      )}

      {/* Collections Tab — every collection the website shows today */}
      {activeTab === "Collections" && <CollectionGrid collections={collections} storeId={selectedStoreId} />}

      {/* Rooms Tab — dates → what is free → hold a room with a deposit */}
      {activeTab === "Rooms" && brandId && (
        <BrandRoomsSection brandId={brandId} storeId={selectedStoreId} brandName={brandName} />
      )}

      {/* Services Tab — bookable service cards (Book → attached form / WhatsApp) */}
      {activeTab === "Services" && brandId && (
        <BrandServicesSection
          brandId={brandId}
          storeId={selectedStoreId}
          brandName={brandName}
          whatsappNumber={
            brandData?.socialLinks?.whatsapp ||
            brandData?.whatsappNumber ||
            brandData?.phone ||
            undefined
          }
        />
      )}
    </>
  );
}
